import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from "react-router-dom"
import Home from "./components/Home"
import Gallery from "./components/Gallery"
import PaintingDetail from "./components/PaintingDetail"
import { AdminPage } from './components/AdminPage'
import Login from './components/Login'
import AuthCallback from './components/AuthCallback'
import Series from './components/Series'
import ExhibitionsAwards from './components/ExhibitionsAwards'
import Colors from './components/Colors'
import PaintingList from './components/PaintingList'
import News from "./components/News"
import Impressum from "./components/Impressum"
import Datenschutz from "./components/Datenschutz"
import { HiOutlineBars3, HiOutlineXMark } from 'react-icons/hi2'
import { createPortal } from 'react-dom'
import { CookieConsentBanner } from './components/common/CookieConsentBanner'

const API_URL = import.meta.env.VITE_API_URL

function App() {
  const { t, i18n } = useTranslation()
  const [paintings, setPaintings] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [darkMode, setDarkMode] = useState(() => {
    // Check if functional cookies are allowed before reading from localStorage
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme === "dark";
    }
    // Default to dark mode instead of system preference
    return true;
  })
  const [navOpen, setNavOpen] = useState(false)
  const [homeContent, setHomeContent] = useState(null)

  // Home-Inhalt laden (für Nav-Subtitle)
  useEffect(() => {
    fetch(`${API_URL}/home`).then(res => res.ok ? res.json() : null).then(data => setHomeContent(data)).catch(() => setHomeContent(null))
  }, [])

  // Dark/Light-Mode Klasse auf <html> setzen
  useEffect(() => {
    const root = window.document.documentElement
    if (darkMode) {
      root.classList.add("dark")
      // Only save to localStorage if functional cookies are allowed
      try {
        localStorage.setItem("theme", "dark")
      } catch (e) {
        // Ignore localStorage errors
      }
    } else {
      root.classList.remove("dark")
      try {
        localStorage.setItem("theme", "light")
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  }, [darkMode])

  useEffect(() => {
    fetch(`${API_URL}/paintings`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`)
        }
        return res.json()
      })
      .then(data => {
        setPaintings(data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Debug: Log when admin route is rendered
  useEffect(() => {
    const token = localStorage.getItem("peart_auth_token");
    console.log("App component - Token status:", token ? "present" : "missing");
  }, []);

  return (
    <Router>
        <CookieConsentBanner />
      <div className="min-h-screen bg-gray-100 dark:bg-zinc-900 transition-colors duration-300 flex flex-col w-full">
      {/* Header */}
        <header className="bg-white/80 dark:bg-zinc-800/80 shadow-md py-6 px-4 md:px-12 lg:px-24 flex flex-col md:flex-row items-center justify-between sticky top-0 z-50 backdrop-blur-sm">
        <div className="flex flex-col items-center md:items-start">
            <Link to="/" className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight text-gray-900 dark:text-gray-100 mb-2">
              PeART
            </Link>
            <p className="text-lg md:text-xl text-gray-600 max-w-xl text-center md:text-left dark:text-gray-300">
              {homeContent && (i18n.language.startsWith('de') ? homeContent.nav_subtitle_de : homeContent.nav_subtitle_en)
                ? (i18n.language.startsWith('de') ? homeContent.nav_subtitle_de : homeContent.nav_subtitle_en)
                : t('header.subtitle')}
          </p>
        </div>
          {/* Hamburger Button (nur auf Mobile sichtbar) */}
          <button
            className="lg:hidden absolute right-6 top-6 z-50 p-2 rounded border border-gray-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 shadow focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
            onClick={() => setNavOpen(o => !o)}
            aria-label={navOpen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {navOpen ? <HiOutlineXMark className="w-7 h-7 text-gray-900 dark:text-gray-100" /> : <HiOutlineBars3 className="w-7 h-7 text-gray-900 dark:text-gray-100" />}
          </button>
          {/* Navigation */}
          <nav className="mt-4 md:mt-0 flex items-center gap-6">
            {/* Desktop Nav */}
            <div className="gap-4 text-lg hidden lg:flex">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.home')}
              </Link>
              <Link to="/gallery" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.gallery')}
              </Link>
              <Link to="/exhibitions-awards" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.exhibitionsAwards')}
              </Link>
              <Link to="/series" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.series')}
              </Link>
              <Link to="/colors" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.colors', 'Farben')}
              </Link>
              <Link to="/news" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                {t('nav.news')}
              </Link>
            </div>
            {/* Mobile Nav Panel: Überdeckt die ganze Seite, Inhalte am rechten Rand */}
            {navOpen && createPortal(
              <div className={`fixed inset-0 bg-white dark:bg-zinc-900 shadow-lg z-[9999] flex flex-col items-end justify-start gap-6 p-8 pt-24 transition-transform duration-300 lg:hidden`}
                style={{ pointerEvents: navOpen ? 'auto' : 'none' }}
                aria-hidden={!navOpen}
              >
                {/* Close Button oben rechts */}
                <button
                  className="absolute top-6 right-6 p-2 rounded border border-gray-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 shadow text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-400 z-[10000]"
                  onClick={() => setNavOpen(false)}
                  aria-label="Menü schließen"
                >
                  <HiOutlineXMark className="w-7 h-7" />
                </button>
                <Link to="/" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.home')}
                </Link>
                <Link to="/gallery" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.gallery')}
                </Link>
                <Link to="/exhibitions-awards" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.exhibitionsAwards')}
                </Link>
                <Link to="/series" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.series')}
                </Link>
                <Link to="/colors" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.colors', 'Farben')}
                </Link>
                <Link to="/news" className="text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white text-2xl font-bold transition-colors" onClick={() => setNavOpen(false)}>
                  {t('nav.news')}
                </Link>
                <div className="flex gap-4 mt-8">
                  <button
                    onClick={() => { setDarkMode((d) => !d); setNavOpen(false); }}
                    className="rounded-full p-2 border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow hover:scale-110 transition"
                    title={darkMode ? "Light Mode" : "Dark Mode"}
                    aria-label="Toggle dark mode"
                  >
                    {darkMode ? (
                      <span role="img" aria-label="Sonne">🌞</span>
                    ) : (
                      <span role="img" aria-label="Mond">🌙</span>
                    )}
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de'); setNavOpen(false); }}
                    className="rounded-full p-2 border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow hover:scale-110 transition"
                    title={i18n.language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
                    aria-label="Toggle language"
                  >
                    {i18n.language === 'de' ? '🇬🇧' : '🇩🇪'}
                  </button>
                </div>
              </div>, document.body
            )}
            {/* Desktop: Darkmode/Language Buttons */}
            <div className="gap-2 items-center hidden lg:flex">
          <button
            onClick={() => setDarkMode((d) => !d)}
            className="rounded-full p-2 border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow hover:scale-110 transition"
            title={darkMode ? "Light Mode" : "Dark Mode"}
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <span role="img" aria-label="Sonne">🌞</span>
            ) : (
              <span role="img" aria-label="Mond">🌙</span>
            )}
          </button>
              <button
                onClick={() => i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de')}
                className="rounded-full p-2 border border-gray-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow hover:scale-110 transition"
                title={i18n.language === 'de' ? 'Switch to English' : 'Zu Deutsch wechseln'}
                aria-label="Toggle language"
              >
                {i18n.language === 'de' ? '🇬🇧' : '🇩🇪'}
              </button>
                    </div>
          </nav>
        </header>
        {/* Main Content */}
        <main className="flex-1 w-full">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/series" element={<Series />} />
            <Route path="/exhibitions-awards" element={<ExhibitionsAwards />} />
            <Route path="/colors" element={<Colors />} />
            <Route path="/gallery/:id" element={<PaintingDetail />} />
            <Route path="/colors/:id" element={<PaintingList />} />
            <Route path="/awards/:id" element={<PaintingList />} />
            <Route path="/series/:id" element={<PaintingList />} />
            <Route path="/exhibitions/:id" element={<PaintingList />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/oauth-redirect" element={<AuthCallback />} />
            <Route path="/news" element={<News />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
      </main>

      {/* Footer */}
        <footer className="bg-white/80 dark:bg-zinc-800/80 text-gray-600 dark:text-gray-300 text-center py-4 mt-auto border-t dark:border-zinc-700 w-full">
          <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
            <div className="space-y-2">
              {/* Erste Zeile: Copyright und Social */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                <span className="text-sm md:text-base">&copy; {new Date().getFullYear()} Petra Maria Mebes – {t('footer.portfolio')}</span>
                <span className="hidden sm:inline text-gray-400">|</span>
                <div className="flex items-center gap-2 sm:gap-4">
                  <a href="mailto:info@atelierpeart.com" className="hover:underline text-sm md:text-base">{t('footer.contact')}</a>
                  <span className="text-gray-400">|</span>
                  <a href="https://instagram.com/pe_art_/" target="_blank" rel="noopener noreferrer" className="hover:underline text-sm md:text-base">Instagram</a>
                  <span className="text-gray-400">|</span>
                  <a href="https://facebook.com/gold.wasser.14/" target="_blank" rel="noopener noreferrer" className="hover:underline text-sm md:text-base">Facebook</a>
                </div>
              </div>
              {/* Zweite Zeile: Legal */}
              <div className="flex items-center justify-center gap-4">
                <Link to="/impressum" className="hover:underline text-sm md:text-base text-gray-500 dark:text-gray-400">{t('footer.impressum')}</Link>
                <span className="text-gray-400">|</span>
                <Link to="/datenschutz" className="hover:underline text-sm md:text-base text-gray-500 dark:text-gray-400">{t('footer.privacy')}</Link>
              </div>
            </div>
          </div>
        </footer>
    </div>
    </Router>
  )
}

export default App